import React, { useEffect, useRef, useState, useCallback } from 'react';

// 全站统一的看图放大层。
// 关闭方式：点任意位置、× 、Esc、以及**手机返回手势 / 浏览器后退**。
// 后退能关是靠打开时 pushState 压一条历史，popstate 时关闭；用别的方式关时再 back() 把这条弹掉，
// 保证不会在历史里留下垃圾、也不会一次返回退出整个页面。
export function Lightbox({ images, index = 0, onClose, onIndex }) {
  const [inner, setInner] = useState(index);
  const cur = onIndex ? index : inner;
  const setCur = onIndex || setInner;

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const byPop = useRef(false);

  useEffect(() => {
    window.history.pushState({ __lightbox: true }, '');
    const onPop = () => { byPop.current = true; closeRef.current(); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // 不是被"后退"关掉的，就把之前压进去的那条历史弹掉
      if (!byPop.current && window.history.state?.__lightbox) window.history.back();
    };
  }, []);

  const step = useCallback((d) => {
    if (!images?.length) return;
    setCur((cur + d + images.length) % images.length);
  }, [cur, images, setCur]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeRef.current();
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step]);

  if (!images || !images.length) return null;
  const nav = (d) => (e) => { e.stopPropagation(); step(d); };

  return (
    <div style={s.overlay} onClick={onClose}>
      <button style={s.close} onClick={onClose}>×</button>
      <img src={images[cur]} alt="" style={s.img} onClick={onClose} />
      {images.length > 1 && (
        <>
          <button style={{ ...s.nav, left: 8 }} onClick={nav(-1)}>‹</button>
          <button style={{ ...s.nav, right: 8 }} onClick={nav(1)}>›</button>
          <div style={s.count}>{cur + 1} / {images.length}</div>
        </>
      )}
      <div style={s.tip}>点任意位置或返回键关闭</div>
    </div>
  );
}

// 用法：const img = useLightbox(); ... <img onClick={() => img.open(images, i)} /> {img.node}
export function useLightbox() {
  const [state, setState] = useState(null);
  const open = useCallback((images, index = 0) => {
    const list = (images || []).filter(Boolean);
    if (list.length) setState({ images: list, index });
  }, []);
  const close = useCallback(() => setState(null), []);
  const node = state ? (
    <Lightbox
      images={state.images}
      index={state.index}
      onIndex={(i) => setState((p) => (p ? { ...p, index: i } : p))}
      onClose={close}
    />
  ) : null;
  return { open, close, node };
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  img: { maxWidth: '96vw', maxHeight: '88vh', objectFit: 'contain' },
  close: {
    position: 'absolute', top: 10, right: 12, width: 36, height: 36,
    background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
    borderRadius: 18, fontSize: 24, lineHeight: '34px', cursor: 'pointer',
  },
  nav: {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    width: 40, height: 56, background: 'rgba(255,255,255,0.15)', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 28, cursor: 'pointer',
  },
  count: { position: 'absolute', top: 16, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 13 },
  tip: { position: 'absolute', bottom: 18, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 12 },
};

export default Lightbox;
